import { CarWashCard } from "../components/CarWash/CarWashCard";
import { useEffect, useState } from "react";
import { CarWashSelected } from "../components/CarWash/CarWashSelected";
import { CarWashPackage } from "../types/carWash.types";
import { useSearchParams } from "react-router-dom";
import { RootState } from "../redux/store";
import { useDispatch, useSelector } from "react-redux";
import { getPackages } from "../redux/slices/carWashSlice";
import { CarWashSelectedSkeleton } from "../components/CarWash/CarWashSelectedSkeleton";
import { CarWashCardSkeleton } from "../components/CarWash/CarWashCardSkeleton";
import { bookCarWash } from "../services/washBooking.service";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { createOrder, verifyPayment } from "../services/payment.service";

export default function CarWash() {
  const [searchParams, setSearchParam] = useSearchParams();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const washPackages = useSelector((state: RootState) => state?.wash);
  const user = useSelector((state: RootState) => state?.auth?.user);
  const city = useSelector((state: RootState) => state?.city);
  const slots = useSelector((state: RootState) => state?.slots);

  const [selectedPackage, setSelectedPackage] = useState<CarWashPackage | null>(
    null,
  );
  const [isLoadingPackage, setIsLoadingPackage] = useState(false);

  useEffect(() => {
    if (!washPackages?.packages?.length) {
      dispatch(getPackages() as any);
    }
  }, []);

  useEffect(() => {
    const washUID = searchParams.get("washId");
    setIsLoadingPackage(
      (washUID &&
        (!washPackages?.packages ||
          washPackages.packages.length === 0)) as boolean,
    );

    const selectedProduct = washPackages?.packages?.find(
      (pkg) => pkg?._id === washUID,
    );
    setSelectedPackage(selectedProduct as CarWashPackage);
  }, [searchParams, washPackages?.packages?.length]);

  const handlePackageSelect = (selectedPkg: CarWashPackage) => {
    setSelectedPackage(selectedPkg);
    setSearchParam({ washId: selectedPkg?._id });
  };

  const handlePaymentSuccess = async (response: any, carId?: string) => {
    try {
      const { data: verifyData, error: verifyError } =
        await verifyPayment(response);

      if (verifyError || !verifyData?.success) {
        toast.error(t("Payment verification failed"));
        return;
      }

      const { error: bookingError } = await bookCarWash(
        selectedPackage?._id as string,
        city?.selectedCity?._id as string,
        carId as string,
        slots?._id as string,
        slots?.date as string,
        slots?.lockedSlot?.slotTime as string,
        selectedPackage?.priceTo as string,
        city?.selectedCity?.city as string,
      );

      if (bookingError) {
        toast.error(t("Payment successful but booking failed"));
        return;
      }

      toast.success(t("Car wash booked successfully!"));
    } catch (err) {
      console.error(err);
      toast.error(t("Something went wrong"));
    }
  };

  const handleBooking = async (option: {
    date: string;
    time: string;
    price: number;
    carId?: string;
  }) => {
    const { data, error } = await createOrder(option.price);

    if (error || !data) {
      toast.error(t("Unable to initiate payment"));
      return;
    }

    const options = {
      key: data.key,
      order_id: data.orderId,
      amount: data.amount,
      currency: "INR",
      name: "Autocare",

      handler: (response: any) => handlePaymentSuccess(response, option.carId),

      modal: {
        ondismiss: () => {
          toast.error(t("Booking cancelled, Payment failed"));
        },
      },

      theme: {
        color: localStorage.getItem("theme") === "dark" ? "#facc15" : "#3b82f6",
      },
    };

    const rzp = new (window as any).Razorpay(options);
    rzp.open();
  };

  return !selectedPackage ? (
    isLoadingPackage ? (
      <CarWashSelectedSkeleton />
    ) : washPackages?.isLoading ? (
      <div className="container mx-auto px-6 animate-fade-in-up mt-5">
        <div className="grid auto-rows-auto grid-cols-[repeat(auto-fill,_minmax(18rem,_1fr))] gap-6">
          {Array.from({ length: 10 }).map((_, i) => (
            <CarWashCardSkeleton key={i} />
          ))}
        </div>
      </div>
    ) : (
      <div className="container mx-auto px-6 animate-fade-in-up mt-5">
        <div className="grid auto-rows-auto grid-cols-[repeat(auto-fill,_minmax(18rem,_1fr))] gap-6">
          {Array.isArray(washPackages?.packages) &&
            washPackages.packages.length > 0 &&
            washPackages.packages.map((pkg, index) => (
              <CarWashCard
                key={index}
                washPkg={pkg}
                handleClick={handlePackageSelect}
              />
            ))}
        </div>
      </div>
    )
  ) : (
    <CarWashSelected
      handleClick={handlePackageSelect}
      selectedPackage={selectedPackage}
      handleBooking={handleBooking}
    />
  );
}
